import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// Query Keys
export const reviewKeys = {
  all: ['reviews'],
  lists: () => [...reviewKeys.all, 'list'],
  list: (filters) => [...reviewKeys.lists(), { filters }],
  details: () => [...reviewKeys.all, 'detail'],
  detail: (id) => [...reviewKeys.details(), id],
};

// API Functions
const reviewApi = {
  // Get all reviews for current client
  getClientReviews: async (params = {}) => {
    const response = await api.get('/reviews', { params });
    return response.data;
  },

  // Get review by ID
  getReview: async (id) => {
    const response = await api.get(`/reviews/${id}`);
    return response.data;
  },

  // Create review
  createReview: async (reviewData) => {
    const response = await api.post('/reviews', reviewData);
    return response.data;
  },

  // Update review
  updateReview: async ({ id, data }) => {
    const response = await api.put(`/reviews/${id}`, data);
    return response.data;
  },

  // Delete review
  deleteReview: async (id) => {
    const response = await api.delete(`/reviews/${id}`);
    return response.data;
  },
};

// React Query Hooks
export const useClientReviews = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [...reviewKeys.list(params), params],
    queryFn: () => reviewApi.getClientReviews(params),
    ...options
  });
};

export const useReview = (id, options = {}) => {
  return useQuery({
    queryKey: [...reviewKeys.detail(id)],
    queryFn: () => reviewApi.getReview(id),
    enabled: !!id,
    ...options
  });
};

export const useCreateReview = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: reviewApi.createReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
    ...options
  });
};

export const useUpdateReview = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: reviewApi.updateReview,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      queryClient.invalidateQueries({ queryKey: reviewKeys.detail(variables.id) });
    },
    ...options
  });
};

export const useDeleteReview = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: reviewApi.deleteReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
    ...options
  });
};

